import React, { useCallback, useEffect, useState } from "react";
import { Convert } from "@/models/responses";
import { ColDef, GridApi } from "ag-grid-community";
import { euroFormatterTwoDecimals, paramsEuroFormatter } from "@/utils/formatters";
import Grid, { AggregateFunction, GridDropdownFilter } from "@/lib/organisms/Grid";
import { PeriodName } from "@/models";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { useAllApartmentEntities } from "@/store/slices/apartments";
import { fetchOptions } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";

const AdminGroupedExpensesPage: React.FC = () => {
    const [expenses, setExpenses] = useState<Map<number, Map<number, number>>>(new Map());
    const [gridRows] = useState<{ [key: string]: number | string }[]>([]);
    const [colDefs, setColDefs] = useState<ColDef[]>([]);
    const gridName = "expenses";

    const {
        yearOptions,
        periodOptions,
        selectedPeriod,
        selectedYear,
        groupOptions,
        aggregateMethodOptions,
        selectedGroup,
        expenseCategoriesMap,
        aggregateMethod,
    } = useAppData();
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    const apartmentEntities = useAllApartmentEntities();

    const { setSelectedYear, setSelectedPeriod, setSelectedGroup, setAggregateMethod } = useUserSettingsDispatch();
    const { predicates } = useApartmentFilters();

    const setColumnDefinitions = useCallback(
        (categories: Set<number>) => {
            const columns: ColDef[] = [
                {
                    field: "apartment",
                    headerName: "Apartment",
                    pinned: "left",
                    cellDataType: "text",
                    width: 250,
                    filter: "agTextColumnFilter",
                },
            ];
            categories.forEach((categoryId) => {
                columns.push({
                    field: categoryId.toString(),
                    headerName: expenseCategoriesMap.get(categoryId)?.category,
                    cellDataType: "number",
                    valueFormatter: paramsEuroFormatter,
                    cellClass: "currencyFormat",
                    width: 120,
                    filter: "agNumberColumnFilter",
                    filterValueGetter: (value) => {
                        return Math.round(value.data[categoryId.toString()]);
                    },
                });
            });
            columns.push({
                field: "total",
                headerName: "Total",
                valueFormatter: paramsEuroFormatter,
                pinned: "right",
                cellDataType: "number",
                cellClass: "currencyFormat",
                width: 120,
                filter: "agNumberColumnFilter",
                filterValueGetter: (value) => {
                    return Math.round(value.data["total"]);
                },
            });
            setColDefs(columns);
        },
        [expenseCategoriesMap],
    );

    const setRows = useCallback(
        (expensesByApartment: Map<number, Map<number, number>>) => {
            let rows: { [key: string]: number | string }[] = [];

            expensesByApartment.forEach((expenseMap, apartmentId) => {
                const apartment = apartmentEntities[apartmentId];
                // Apply the shared apartment filters: skip any apartment that
                // doesn't satisfy every active predicate (Bedrooms, Municipality
                // Section, Owner Group, Tags). Apartments missing from the
                // entity map are also dropped — without an apartment record we
                // can't resolve a name and shouldn't render a ghost row.
                if (!apartment || !predicates.every((p) => p(apartment))) {
                    return;
                }
                let row: { [key: string]: number | string } = {
                    total: 0,
                    total_average: 0,
                };
                row["id"] = apartmentId;
                row["apartment"] = apartment.name;

                expenseMap.forEach((amount, categoryId) => {
                    let categoryName = categoryId.toString();
                    row[categoryName] = amount;
                    row["total"] = (row["total"] as number) + amount;
                });
                rows.push(row);
            });
            if (gridApi) {
                gridApi.setGridOption(
                    "rowData",
                    rows.sort((a, b) => (a.apartment as string).localeCompare(b.apartment as string)),
                );
            }
        },
        [apartmentEntities, gridApi, predicates],
    );

    const getAndSetExpenses = useCallback(
        (year: string, period: string, agg: string): void => {
            let url = `${apiUrl}/api/admin/apartments/expenses?aggregate=${agg}&year=${year}&period=${period}`;
            fetch(url, fetchOptions)
                .then((response) => {
                    if (response) {
                        return response.text();
                    }
                    throw new Error("Unauthorized");
                })
                .then((response) => {
                    let expenses = Convert.toGetGroupedByExpensesResponse(response).expenses;
                    const expensesByApartment = new Map<number, Map<number, number>>();
                    const categories = new Set<number>();
                    expenses.forEach((expense) => {
                        if (!expensesByApartment.has(expense.apartmentId)) {
                            expensesByApartment.set(expense.apartmentId, new Map());
                        }
                        categories.add(expense.categoryId);
                        expensesByApartment.get(expense.apartmentId)?.set(expense.categoryId, expense.amount);
                    });
                    setColumnDefinitions(categories);
                    setExpenses(expensesByApartment);
                })
                .catch((error) => console.log(error));
        },
        [setColumnDefinitions],
    );

    useEffect(() => {
        if (selectedGroup) {
            const filteredExpenses = new Map<number, Map<number, number>>();
            new Map<number, Map<number, number>>();
            expenses.forEach((expenseMap, apartmentId) => {
                if (apartmentEntities[apartmentId]?.groupId === selectedGroup) {
                    filteredExpenses.set(apartmentId, expenseMap);
                }
            });
            setRows(filteredExpenses);
        } else {
            setRows(expenses);
        }
    }, [selectedGroup, expenses, selectedYear, expenseCategoriesMap, setRows, apartmentEntities]);

    useEffect(() => {
        getAndSetExpenses(selectedYear.toString(), selectedPeriod, aggregateMethod);
    }, [selectedPeriod, selectedYear, apartmentEntities, yearOptions, getAndSetExpenses, aggregateMethod]);

    const gridDropdownFilters: GridDropdownFilter[] = [
        {
            label: "Period",
            options: periodOptions,
            selectedOption: selectedPeriod,
            onSelectionChanged: (period) => setSelectedPeriod(period as PeriodName),
        },
        {
            label: "Year",
            options: yearOptions,
            selectedOption: selectedYear.toString(),
            onSelectionChanged: (year) => setSelectedYear(Number(year)),
        },
        {
            label: "Group",
            options: groupOptions,
            selectedOption: selectedGroup.toString(),
            onSelectionChanged: (apartment) => setSelectedGroup(Number(apartment)),
        },
        {
            label: "AggregateMethod",
            options: aggregateMethodOptions,
            selectedOption: aggregateMethod,
            onSelectionChanged: (aggregateMethod) => setAggregateMethod(aggregateMethod as "sum" | "avg"),
        },
    ];

    return (
        <div className="flex flex-col space-y-2 p-2 py-0" data-testid="admin-group-expenses-page">
            <ApartmentFilterBar />
            <Grid<{ [key: string]: number | string }>
                columnDefs={colDefs}
                gridName={gridName + " " + aggregateMethod}
                initialRowData={gridRows}
                onGridReady={setGridApi}
                dropdownFilters={gridDropdownFilters}
                showAggregateFilter={true}
                getRowId={(params) => params.data.apartment as string}
                columnsToAggregate={[
                    ...colDefs
                        .filter((col) => col.headerName !== "Apartment")
                        .map((col) => {
                            return {
                                colDef: col,
                                aggregateFunction: "sum" as AggregateFunction,
                                formatter: euroFormatterTwoDecimals,
                            };
                        }),
                    ...colDefs
                        .filter((col) => col.headerName !== "Apartment")
                        .map((col) => {
                            return {
                                colDef: col,
                                aggregateFunction: "avg" as AggregateFunction,
                                formatter: euroFormatterTwoDecimals,
                            };
                        }),
                ]}
            />
        </div>
    );
};

export default AdminGroupedExpensesPage;
